import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';

import { loadingAuthAtom } from '../../states/atoms';

const resolveRedirectTarget = (): string | null => {
  const stored = sessionStorage.getItem('auth_redirect');
  if (!stored) return null;

  sessionStorage.removeItem('auth_redirect');

  try {
    const url = new URL(stored, window.location.origin);
    if (url.origin !== window.location.origin) return null;

    const path = url.pathname + url.search;
    if (!path || path === '/') return null;

    return path;
  } catch {
    return null;
  }
};

const RequireAuth = ({ children }: any) => {
  const { isAuthenticated, isLoading } = useAuth0();
  const [loading, setLoading] = useRecoilState(loadingAuthAtom);
  const location = useLocation();
  const navigate = useNavigate();
  const savedRef = useRef(false);
  const redirectedRef = useRef(false);

  useEffect(() => {
    if (!isLoading) setTimeout(() => setLoading(false), 500);
  }, [isLoading]);

  // Save the intended destination as soon as Auth0 confirms the user is NOT
  // authenticated. useRef ensures we only write once per mount.
  if (!isLoading && !isAuthenticated && !savedRef.current) {
    const { pathname, search } = location;
    if (pathname && pathname !== '/') {
      sessionStorage.setItem('auth_redirect', pathname + search);
    }
    savedRef.current = true;
  }

  // After login, Auth0 redirects to /dashboard (the redirect_uri). CheckAuth
  // never runs in that flow. Consume the saved deep link here instead.
  useEffect(() => {
    if (isAuthenticated && !redirectedRef.current) {
      redirectedRef.current = true;
      const target = resolveRedirectTarget();
      if (target) {
        navigate(target, { replace: true });
      }
    }
  }, [isAuthenticated]);

  if (!isAuthenticated && !loading) {
    return (
      <Navigate
        to={{
          pathname: '/',
        }}
        replace={true}
      />
    );
  }

  return <>{children}</>;
};

export default RequireAuth;
